import React from "react";
import RecurrenceEditor from "./RecurrenceEditor";
import {
  DIRECTIONS,
  FREQUENCIES,
  RECIPIENT_MODES,
  TEMPLATES,
  TIMEZONE_OPTIONS,
  emptyRecurrence,
} from "./reminderConstants";

/**
 * Presentational field grid for the reminder-rule form. Holds no state — it
 * renders `form` and reports every change through `onChange(patch)`.
 *
 * The fields are split across the two creation steps: "target" asks what the
 * reminder watches, "delivery" asks when it fires and who receives it.
 * @param {object} props
 * @param {object} props.form the current form state.
 * @param {(patch: object) => void} props.onChange applies a partial update.
 * @param {Array<object>} props.savedQueries saved queries to choose from.
 * @param {string} [props.recipientLabel] what the dataset calls its auto-resolved
 *   recipient ("Data Owner", "Auditor"); falls back to the generic label.
 * @param {string} props.step which step's fields to render ("target" | "delivery").
 * @returns {JSX.Element}
 */
export default function ReminderRuleFields({
  form,
  onChange,
  savedQueries,
  recipientLabel,
  step,
}) {
  if (step === "target") {
    return (
      <div className="rem-grid">
        <label className="rem-field">
          <span className="rem-label">Name</span>
          <input
            className="rem-input"
            value={form.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="e.g. 7 days before due"
          />
        </label>

        <label className="rem-field">
          <span className="rem-label">Saved query</span>
          <select
            className="rem-select"
            value={form.savedQueryId}
            onChange={(e) => onChange({ savedQueryId: e.target.value })}
          >
            <option value="">Select a saved query…</option>
            {savedQueries.map((q) => {
              const summarised = !!q.config?.aggregations?.length;
              return (
                <option key={q.id} value={q.id} disabled={summarised}>
                  {summarised ? `${q.name} (summarised — not usable)` : q.name}
                </option>
              );
            })}
          </select>
        </label>

        <p className="rem-hint">
          Reminders fire relative to each KPI's due date, set on the Set Due Date
          screen.
        </p>
      </div>
    );
  }

  return (
    <div className="rem-grid">
      <label className="rem-field">
        <span className="rem-label">Offset (days)</span>
        <div className="rem-inline">
          <input
            className="rem-input rem-narrow"
            type="number"
            min="0"
            value={form.offsetDays}
            onChange={(e) => onChange({ offsetDays: e.target.value })}
          />
          <select
            className="rem-select"
            value={form.direction}
            onChange={(e) => onChange({ direction: e.target.value })}
          >
            {DIRECTIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </div>
      </label>

      <label className="rem-field">
        <span className="rem-label">Frequency</span>
        <select
          className="rem-select"
          value={form.frequency}
          onChange={(e) => onChange({ frequency: e.target.value })}
        >
          {FREQUENCIES.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
      </label>

      {form.frequency === "custom" && (
        <div className="rem-field rem-field-wide">
          <RecurrenceEditor
            value={form.recurrence || emptyRecurrence()}
            onChange={(patch) =>
              onChange({
                recurrence: {
                  ...(form.recurrence || emptyRecurrence()),
                  ...patch,
                },
              })
            }
          />
        </div>
      )}

      <label className="rem-field">
        <span className="rem-label">Send time</span>
        <input
          className="rem-input"
          type="time"
          value={form.sendTime}
          onChange={(e) => onChange({ sendTime: e.target.value })}
        />
      </label>

      <label className="rem-field">
        <span className="rem-label">Timezone</span>
        <select
          className="rem-select"
          value={form.timezone}
          onChange={(e) => onChange({ timezone: e.target.value })}
        >
          {TIMEZONE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      <label className="rem-field">
        <span className="rem-label">Recipients</span>
        <select
          className="rem-select"
          value={form.recipientMode}
          onChange={(e) => onChange({ recipientMode: e.target.value })}
        >
          {RECIPIENT_MODES.map((m) => (
            <option key={m.value} value={m.value}>
              {m.value === "owner" && recipientLabel
                ? `${recipientLabel} (auto-resolved)`
                : m.label}
            </option>
          ))}
        </select>
      </label>

      {form.recipientMode === "explicit" && (
        <label className="rem-field">
          <span className="rem-label">Emails (comma-separated)</span>
          <input
            className="rem-input"
            value={form.explicitRecipients}
            onChange={(e) => onChange({ explicitRecipients: e.target.value })}
            placeholder="a@x.com, b@x.com"
          />
        </label>
      )}

      <label className="rem-field">
        <span className="rem-label">Template</span>
        <select
          className="rem-select"
          value={form.templateKey}
          onChange={(e) => onChange({ templateKey: e.target.value })}
        >
          {TEMPLATES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </label>

      <label className="rem-field rem-checkbox">
        <input
          type="checkbox"
          checked={form.isEnabled}
          onChange={(e) => onChange({ isEnabled: e.target.checked })}
        />
        <span className="rem-label">Enabled</span>
      </label>
    </div>
  );
}
